import * as yup from "yup";
import React, { useEffect } from "react";
import { Form, Button, Loader } from "semantic-ui-react";
import queryString from "query-string";
import { useParams, Redirect, useHistory } from "react-router-dom";
import identitySelectors from "../../../selectors/identity";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers";
import { ControlledInput } from "../../../components/ControlledForm";
import { useVerifyEmail } from "../hooks/useVerifyEmail";
import { useToasts } from "react-toast-notifications";
import { useAuthOTPSend } from "../hooks/useAuthOTPSend";
import classNames from "classnames";
import { useDispatch } from "react-redux";
import { postLoginClear } from "../../../actions/identity";

export default function VerifyEmailForm() {
  const params = useParams<{ site: string }>();
  const { email, otp, redirect } = queryString.parse(window.location.search) as {
    email: string;
    otp: string;
    redirect: string;
  };
  const history = useHistory();
  const dispatch = useDispatch();
  const { addToast } = useToasts();
  const { control, handleSubmit, errors, reset } = useForm<{
    otp: string;
  }>({
    resolver: yupResolver(
      yup.object().shape({
        otp: yup.string().required("Code is required").length(6, "Code must be 6 digits"),
      }),
    ),
    defaultValues: {
      otp: "",
    },
  });
  const {
    verifyOTP,
    isLoading,
    error: verifyError,
  } = useVerifyEmail({
    onError: (_, message) => {
      addToast(message, { appearance: "error", autoDismiss: true });
    },
  });
  const { sendAuthOTP, remainingSeconds } = useAuthOTPSend({
    onError: (_, message) => {
      addToast(message, {
        appearance: "error",
        autoDismiss: true,
      });
    },
  });

  useEffect(() => {
    if (!email || otp) return;
    sendAuthOTP({
      identifier: email,
      type: "Email",
      returnUrl: `${window.location.origin}/${params.site}/public/verify?email=${encodeURIComponent(email)}`,
    });
  }, []);
  useEffect(() => {
    if (otp) {
      reset({ otp });
      setTimeout(() => {
        handleSubmit(onSubmit)();
      }, 50);
      const newSearch = queryString.stringify({
        email,
        redirect,
      });
      history.replace({
        pathname: `/${params.site}/public/verify`,
        search: newSearch,
      });
    }
  }, [otp, reset, handleSubmit]);
  const resend = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (remainingSeconds > 0) return;
    try {
      await sendAuthOTP({
        identifier: email,
        type: "Email",
        returnUrl: `${window.location.origin}/${params.site}/public/verify?email=${encodeURIComponent(email)}`,
      });
      addToast("Resent verification code.", {
        appearance: "success",
        autoDismiss: true,
      });
    } catch (error) {
      addToast("Failed to resend code. Please try again later.", {
        appearance: "error",
        autoDismiss: true,
      });
    }
  };
  const returnToLogin = () => {
    const redirectUrl = redirect ? `?redirect=${encodeURIComponent(redirect)}` : "";
    dispatch(postLoginClear());
    history.push(`/${params.site}/public/login${redirectUrl}`);
  };
  const onSubmit = async ({ otp }: { otp: string }) => {
    await verifyOTP({
      otp,
      identifier: email,
    });
  };

  const isLoggedIn = identitySelectors.isLoggedIn();
  if (isLoggedIn) {
    return (
      <Redirect
        to={{
          pathname: `/${params.site}/landing`,
          search: queryString.stringify({
            redirect: redirect,
            remember: true,
          }),
        }}
      />
    );
  }

  if (otp && !errors.otp && !verifyError) return <Loader active inline='centered' size='large' />;

  return (
    <>
      <Form>
        <p className='tw-pt-4'>
          {`We've sent a 6-digit code to`} <b>{email}</b>.{" "}
          {`If you don't see the email, check your spam filter or try resending the code.`}
        </p>
        <Form.Field required>
          <ControlledInput
            control={control}
            errors={errors}
            name='otp'
            label='Code'
            type='text'
            autoComplete='one-time-code'
          />
          <div className='tw-flex tw-justify-between'>
            <button
              className={classNames({
                "tw-mt-1 tw-cursor-pointer tw-appearance-none tw-border-none tw-bg-transparent tw-p-0 tw-text-sm tw-font-bold hover:tw-underline": true,
                "tw-text-blue": !remainingSeconds,
                "tw-text-grey": remainingSeconds,
              })}
              disabled={remainingSeconds > 0}
              onClick={resend}
            >
              Resend Code {remainingSeconds ? `(${remainingSeconds}s)` : ""}
            </button>
            <button
              className='tw-mt-1 tw-cursor-pointer tw-appearance-none tw-border-none tw-bg-transparent tw-p-0 tw-text-sm tw-font-bold tw-text-blue hover:tw-underline'
              onClick={returnToLogin}
            >
              Change Email Address
            </button>
          </div>
        </Form.Field>
        <div className='tw-flex tw-justify-center tw-gap-3'>
          <Button
            primary
            size='large'
            onClick={handleSubmit(onSubmit)}
            className='tw-mt-4'
            isLoading={isLoading}
            disabled={isLoading}
          >
            Verify
          </Button>
          <Button secondary size='large' onClick={returnToLogin} className='tw-mt-4'>
            Cancel
          </Button>
        </div>
      </Form>
    </>
  );
}
